import { buildSectionSelector, normalizeSectionId } from '@theme/section-renderer';

/**
 * Parsed section HTML shared across `<lazy-content-section>` instances in the same parent section.
 * @type {Map<string, Promise<Document>>}
 */
const sectionDocumentPromises = new Map();

/**
 * E7: classic scripts that have nothing left to do when a lazy section includes them again.
 * Once their runtime is on the page, the copy in the section response is left inert:
 * cx-card-slider.js initialises each loaded section itself (on `lazy-content-section:loaded`),
 * and custom.js is guarded and returns at once. Every other block script still re-runs.
 *
 * @param {HTMLScriptElement} script
 * @returns {boolean}
 */
function isRunOnceScriptOnPage(script) {
  const src = script.getAttribute('src');
  if (!src) return false;
  let path = '';
  try {
    path = new URL(src, document.baseURI).pathname;
  } catch {
    return false;
  }
  const win = /** @type {any} */ (window);
  if (path.endsWith('/cx-card-slider.js')) return typeof win.CX?._runCardSlider === 'function';
  if (path.endsWith('/custom.js')) return win.__cxCustomJsLoaded === true;
  return false;
}

/** @type {WeakMap<HTMLLinkElement, Promise<void>>} */
const stylesheetLoadPromises = new WeakMap();
/** @type {WeakSet<HTMLLinkElement>} */
const failedStylesheetLinks = new WeakSet();

/**
 * CLS-RINGS. With the theme setting "Load theme stylesheets after the page" on, a page with
 * critical CSS prints the theme stylesheets only as noscript copies marked data-css-after-load,
 * and assets/stylesheet-late.js adds their links after each copy at the load event or the first
 * input. The critical CSS styles a lazy section's placeholder, not the blocks its section response
 * brings, so content mounted before those links apply is laid out without its rules and moves
 * when they arrive (the desktop Rings page, "In The Spotlight": a layout shift of about 0.1 in 21
 * of 35 test loads, Oct 2026). A section response that is ready first therefore waits for
 * them. A page without held copies (the setting off) never waits: sections mount as before.
 *
 * CLS-RINGS-RELEASE. stylesheet-late.js (and its inline copy in snippets/stylesheets.liquid) adds
 * those links with media "print" and gives every one its own media back together once all have
 * landed (the release, LATE-CSS-TOGETHER). A landed link at media "print" has its sheet but none of
 * its rules apply, so a held link counts only once it has its media back; the release fires no load
 * or error event, so the wait also checks at each change of a link's media attribute. Counted at
 * its sheet alone, the Rings row mounted between the two and moved twice (layout shift 0.097 in
 * 2 of 3 lab runs at computer width, 29 Oct 2026).
 */

/**
 * At most this long a section response waits, so a stalled release never keeps the placeholder for
 * good. In 710 test loads of test copies of the theme with the setting on (8 pages, phone and
 * desktop, 23 and 24 Oct 2026) the held stylesheets had all arrived 1.8 to 10.0 s after the page
 * was requested (median 3.3 s), and the wait starts only once a response is ready (2.4 s or more
 * into the page on desktop Rings), so the limit is reached only when the release stalls.
 */
const HELD_STYLESHEETS_MAX_WAIT_MS = 10000;

/** @type {string[] | null} */
let heldStylesheetHrefs = null;

/** @type {WeakSet<HTMLLinkElement>} */
const failedHeldStylesheetLinks = new WeakSet();

/**
 * The href of every stylesheet link the held copies stand for, read the way stylesheet-late.js
 * reads them (with scripting on, a noscript element holds its markup as text). Kept once the page
 * is parsed: the copies are never removed. A copy's link for print only (none today: the copies
 * are printed with media "all" or "screen") never styles the screen and is not waited for, so media
 * "print" on a listed link always means held.
 *
 * @returns {string[]}
 */
function readHeldStylesheetHrefs() {
  if (heldStylesheetHrefs) return heldStylesheetHrefs;
  /** @type {string[]} */
  const hrefs = [];
  const holder = document.createElement('template');
  for (const copy of document.querySelectorAll('noscript[data-css-after-load]')) {
    holder.innerHTML = copy.textContent ?? '';
    for (const link of holder.content.querySelectorAll('link[rel="stylesheet"][href]')) {
      if (link.getAttribute('media') === 'print') continue;
      hrefs.push(link.getAttribute('href') ?? '');
    }
  }
  if (document.readyState !== 'loading') heldStylesheetHrefs = hrefs;
  return hrefs;
}

/**
 * A link has settled once it is released (its media is no longer "print") and its sheet is there,
 * or its load has ended without one (an error event seen here, or a finished fetch in Resource
 * Timing for an error that came before).
 *
 * @param {HTMLLinkElement} link
 * @returns {boolean}
 */
function heldLinkSettled(link) {
  if (link.getAttribute('media') === 'print') return false;
  if (link.sheet || failedHeldStylesheetLinks.has(link)) return true;
  return performance.getEntriesByName(link.href, 'resource').length > 0;
}

/** @returns {boolean} every held stylesheet's link is in the document and has settled */
function heldStylesheetsSettled() {
  const links = [...document.querySelectorAll('link[rel="stylesheet"][href]')];
  return readHeldStylesheetHrefs().every((href) =>
    links.some(
      (link) => link instanceof HTMLLinkElement && link.getAttribute('href') === href && heldLinkSettled(link)
    )
  );
}

/**
 * Null when there is nothing to wait for (no held copies, or every one in and settled), so the
 * caller mounts in the same task as before; otherwise a promise that resolves once they have
 * settled, or after HELD_STYLESHEETS_MAX_WAIT_MS.
 *
 * @returns {Promise<void> | null}
 */
function heldStylesheetsPending() {
  if (!readHeldStylesheetHrefs().length || heldStylesheetsSettled()) return null;
  return new Promise((resolve) => {
    // Load and error events do not bubble; a capturing listener on the document sees each link's.
    // Only a held link's own event can change the answer: every other load (images, scripts,
    // frames) returns at once.
    /** @param {Event} event */
    const check = (event) => {
      const link = event.target;
      if (!(link instanceof HTMLLinkElement)) return;
      if (!readHeldStylesheetHrefs().includes(link.getAttribute('href') ?? '')) return;
      if (event.type === 'error') failedHeldStylesheetLinks.add(link);
      if (heldStylesheetsSettled()) done();
    };
    // The release gives the held links their media back with no event of their own: a change of a
    // link's media attribute is checked too.
    const released = new MutationObserver((records) => {
      if (records.some((record) => record.target instanceof HTMLLinkElement) && heldStylesheetsSettled()) done();
    });
    const done = () => {
      clearTimeout(timer);
      released.disconnect();
      document.removeEventListener('load', check, true);
      document.removeEventListener('error', check, true);
      resolve();
    };
    const timer = setTimeout(done, HELD_STYLESHEETS_MAX_WAIT_MS);
    document.addEventListener('load', check, true);
    document.addEventListener('error', check, true);
    released.observe(document, { attributes: true, attributeFilter: ['media'], subtree: true });
  });
}

/**
 * CLS-ONAIR. At most this long content that mounts on screen waits for the stylesheets its own
 * markup links, so a stalled stylesheet never keeps the placeholder for good. On Home the Live TV
 * card's three stylesheets had all arrived 31 to 967 ms after its section response in 25 test
 * loads (24 Oct 2026), so the limit is reached only when one stalls.
 */
const INNER_STYLESHEETS_MAX_WAIT_MS = 3000;

/**
 * HOME-TASK. Resolves in a task of its own. Every element that shares one section response
 * waits for it after the response is parsed and before it mounts, so the parse and each
 * element's mount run as separate tasks instead of one. On Home the six containers of the
 * scheduled section share one 536 KB response; on the two desktop runs of 28 Oct 2026 (London,
 * the copy with the eight app embeds off) the page's one long task after the first paint
 * (171 and 201 ms) began within 40 ms of that response's end.
 *
 * @returns {Promise<void>}
 */
function nextTask() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * HOME-MOUNT-STEPS. An element that carries data-mount-steps (a lazy CX Container block with the theme
 * setting perf_cx_lazy_mount_steps on, its default; printed by snippets/cx-container-lazy-inner.liquid
 * for blocks/cx-container.liquid) puts its content in over several frames instead of in one task. On
 * Home the long task after the scheduled section's response is the style, layout and paint of the
 * content just put in, not its parse: in a trace of a test copy at a CPU slowdown of 6 (30 Oct 2026, the
 * speed of the monitor's test machine), the frame after the first container went in took 180.4 ms (style
 * 100.0 ms over 1,233 elements, layout 43.0, paint 12.0), the parse of the whole response 38.6 ms. The
 * nodes, their order and their attributes are the same; only the frames they arrive in change. Without
 * the attribute an element mounts in one task, as before.
 *
 * At most this many elements go in a frame. Measured on the test copy's saved Home at the same slowdown
 * (offline, 30 Oct 2026): steps of up to 250 elements made frames of 38 to 51 ms, steps of up to 150
 * elements frames of 31 to 48 ms, under the 50 ms that makes a long task.
 */
const MOUNT_STEP_MAX_ELEMENTS = 150;

/**
 * At most this long a step waits for the next frame, so a frame that does not come never holds the
 * rest of the content back: six frames at 60 frames a second.
 */
const MOUNT_STEP_FRAME_WAIT_MS = 100;

/**
 * Elements that may go in before their children: plain containers with no behaviour of their own.
 * Anything else, a custom element above all, goes in with everything inside it.
 */
const MOUNT_STEP_SPLIT_TAGS = new Set(['a', 'article', 'aside', 'div', 'figure', 'li', 'ol', 'section', 'ul']);

/**
 * One element's content goes in at a time, and the next element starts a frame after the last step of
 * the one before, so the steps of two elements never share a frame.
 * @type {Promise<void>}
 */
let mountStepsQueue = Promise.resolve();

/**
 * Steps are for the load before the shopper's first scroll or input, when the rows below the first
 * screen arrive. Once the shopper scrolls, presses, touches, turns the wheel or types, content goes in
 * at once again, as before: a row that grows over several frames while the shopper scrolls towards it
 * moves what is on screen below it at each step (a store check of a test copy on a phone at the
 * monitor machine's speed, 31 Oct 2026: 0.137 of counted layout shift during a steady scroll, against
 * 0.001 on the live theme). A row already part way in gets the rest in its next step.
 */
let mountStepsStopped = false;

/** Whether the listeners below are on (from the first element with data-mount-steps). */
let mountStepsListening = false;

/** The shopper's inputs that stop the steps, listened for on the window in the capture phase. */
const MOUNT_STEP_STOP_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

function stopMountSteps() {
  mountStepsStopped = true;
  window.removeEventListener('scroll', stopMountSteps);
  for (const type of MOUNT_STEP_STOP_EVENTS) window.removeEventListener(type, stopMountSteps, true);
}

/**
 * @returns {boolean} Whether content may still go in over several frames: no scroll or input yet,
 * and the page still at its top (a scroll before the listeners were on). Called right after a box
 * read, so the scroll offset costs no layout of its own. The first call puts the listeners on, so a
 * page without data-mount-steps never has them.
 */
function mountStepsAllowed() {
  if (mountStepsStopped) return false;
  if (window.scrollY !== 0) {
    stopMountSteps();
    return false;
  }
  if (!mountStepsListening) {
    mountStepsListening = true;
    // The page's own scroll only: an element's scroll (a slider) does not reach the window's bubble phase.
    window.addEventListener('scroll', stopMountSteps, { passive: true });
    for (const type of MOUNT_STEP_STOP_EVENTS) {
      window.addEventListener(type, stopMountSteps, { capture: true, passive: true });
    }
  }
  return true;
}

/**
 * The gap between two elements' steps: a frame while steps go on, none once they have stopped.
 *
 * @returns {Promise<void>}
 */
function mountStepsGap() {
  return mountStepsStopped ? Promise.resolve() : afterNextFrame();
}

/**
 * Resolves in a task after the next frame, so what went in before it has been styled, laid out and
 * painted in that frame; or after MOUNT_STEP_FRAME_WAIT_MS. At once while the document is hidden: no
 * frame is painted then, so there is nothing to spread.
 *
 * @returns {Promise<void>}
 */
function afterNextFrame() {
  if (document.visibilityState === 'hidden') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, MOUNT_STEP_FRAME_WAIT_MS);
    requestAnimationFrame(() => {
      setTimeout(() => {
        clearTimeout(timer);
        resolve();
      }, 0);
    });
  });
}

/**
 * A style element or a stylesheet link. These go in with the element that holds them, ahead of its other
 * children, so their rules apply from its first frame: a product slider prints its phone grid rule (only
 * the first cards show) after its cards.
 *
 * @param {Node} node
 * @returns {boolean}
 */
function isMountStyle(node) {
  if (!(node instanceof Element)) return false;
  if (node.localName === 'style') return true;
  return node.localName === 'link' && /(^|\s)stylesheet(\s|$)/i.test(node.getAttribute('rel') ?? '');
}

/**
 * @param {Node} node
 * @returns {number} The node's element count, itself included (0 for a text or comment node).
 */
function mountElementCount(node) {
  return node instanceof Element ? 1 + node.getElementsByTagName('*').length : 0;
}

/**
 * @typedef {{ parent: Node, node: Node, before: Node | null, size: number }} MountUnit
 * One unit of content: node goes into parent before the node before (at the end when null); size
 * counts its elements.
 */

/**
 * Lists the units content goes in by, in document order. A node goes in whole, with everything inside
 * it, unless it is a plain container (MOUNT_STEP_SPLIT_TAGS) holding more than MOUNT_STEP_MAX_ELEMENTS
 * elements: that container goes in holding only its style children (isMountStyle), and its other
 * children follow as units of their own. Style children are not listed: they go in with their parent.
 * Every unit goes in before the style child that follows it, so the finished order is the same.
 *
 * @param {Node} parent - Where the nodes go.
 * @param {Node[]} nodes - Its children, in document order.
 * @param {MountUnit[]} units - Gets one entry per unit.
 */
function listMountUnits(parent, nodes, units) {
  /** @type {(Node | null)[]} */
  const nextStyle = [];
  let next = null;
  for (let index = nodes.length - 1; index >= 0; index--) {
    nextStyle[index] = next;
    if (isMountStyle(nodes[index])) next = nodes[index];
  }

  for (const [index, node] of nodes.entries()) {
    if (isMountStyle(node)) continue;
    const size = mountElementCount(node);
    if (
      node instanceof Element &&
      size > MOUNT_STEP_MAX_ELEMENTS &&
      MOUNT_STEP_SPLIT_TAGS.has(node.localName) &&
      !node.hasAttribute('is')
    ) {
      const children = [...node.childNodes];
      const styles = children.filter(isMountStyle);
      node.replaceChildren(...styles);
      units.push({ parent, node, before: nextStyle[index], size: mountElementCount(node) });
      listMountUnits(node, children, units);
    } else {
      units.push({ parent, node, before: nextStyle[index], size });
    }
  }
}

/**
 * `<lazy-content-section>` — loads block markup via the Section Rendering API when the element
 * nears the viewport, or uses HTML already present (theme editor / `?section_id=` response).
 *
 * **Storefront**
 * - Initial page response may contain a placeholder only (no block/footer markup in the document).
 * - Fetches `GET <current url>?section_id=<section.id>` and injects `.lazy-content-section__inner` children.
 * - With `data-block-id`, extracts one cx-container block from the cached section response (one fetch per section).
 *
 * **HTML contract**
 * - `data-section-id` (optional): theme section id for Section Rendering API fetch.
 * - `data-block-id` (optional): cx-container block id — scope injection to that block’s lazy inner.
 * - `data-stylesheet` (optional): Comma-separated asset URLs loaded when the element nears the viewport.
 * - `data-script` (optional): asset URL loaded before section HTML is injected (Section Rendering API path).
 * - Placeholder: `[data-lazy-placeholder]` until content loads.
 * - Loaded body: `.lazy-content-section__inner` wrapping block output (from server on AJAX or editor).
 *
 * **Attributes (dataset)**
 * - `data-root-margin` — `IntersectionObserver` `rootMargin` (default `200px 0px`).
 * - `data-load-immediately` — `"true"` loads stylesheet + skips deferred observer (theme editor).
 * - `data-loaded` — content is already in the DOM; stylesheet may still defer until near viewport.
 * - `data-mount-steps`: fetched content goes in over several frames (HOME-MOUNT-STEPS).
 *
 * **Events**
 * - `lazy-content-section:loaded` — bubbles after content is in the document.
 * - `lazy-content-section:error` — bubbles on fetch/parse failure; `event.detail` has `{ error }`.
 *
 * **Public API**
 * - `load()` — eagerly fetch section HTML / stylesheet without waiting for viewport (e.g. mobile drawer footer clone).
 *
 * @customElement lazy-content-section
 * @see https://shopify.dev/docs/api/ajax/section-rendering
 */
class LazyContentSection extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Promise<void> | null} */
  #contentLoadPromise = null;

  /**
   * Eagerly load deferred stylesheet and/or section HTML (skips the viewport observer).
   * Safe to call multiple times; returns the same in-flight promise.
   *
   * @returns {Promise<void>}
   */
  load() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    const stylesheetUrl = this.dataset.stylesheet;
    if (stylesheetUrl) this.#ensureStylesheetsDeferred(stylesheetUrl);
    if (this.dataset.script) this.#ensureScriptDeferred(this.dataset.script);

    if (this.hasAttribute('data-loaded')) {
      return Promise.resolve();
    }

    if (this.dataset.sectionId) {
      return this.#loadFromSectionApi();
    }

    this.#syncLoadedUi();
    return Promise.resolve();
  }

  /** @type {string | undefined} */
  #pendingStylesheetUrl;

  /** @type {boolean} */
  #pendingContentFetch = false;

  /** Wires deferred stylesheet and/or section HTML fetch when near the viewport. */
  connectedCallback() {
    const stylesheetUrl = this.dataset.stylesheet;
    const needsContentFetch = Boolean(this.dataset.sectionId) && !this.hasAttribute('data-loaded');

    this.#pendingStylesheetUrl = stylesheetUrl;
    this.#pendingContentFetch = needsContentFetch;

    if (this.dataset.loadImmediately === 'true') {
      if (stylesheetUrl) this.#ensureStylesheetsDeferred(stylesheetUrl);
      if (this.dataset.script) this.#ensureScriptDeferred(this.dataset.script);
      this.#syncLoadedUi();
      return;
    }

    if (!stylesheetUrl && !needsContentFetch) {
      this.#syncLoadedUi();
      return;
    }

    this.#observeNearViewport(stylesheetUrl, needsContentFetch);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * Loads the deferred stylesheet and/or section HTML immediately, bypassing the
   * near-viewport observer. Safe to call repeatedly. Resolves once content (if any)
   * is in the document, so callers (e.g. anchor jump links) can scroll afterwards.
   *
   * @returns {Promise<void>}
   */
  loadNow() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    if (this.#pendingStylesheetUrl) this.#ensureStylesheetsDeferred(this.#pendingStylesheetUrl);
    if (this.dataset.script) this.#ensureScriptDeferred(this.dataset.script);

    if (this.#pendingContentFetch) return this.#loadFromSectionApi();

    return Promise.resolve();
  }

  /**
   * @param {string | undefined} stylesheetUrl
   * @param {boolean} fetchContent
   */
  #observeNearViewport(stylesheetUrl, fetchContent) {
    const rootMargin = this.dataset.rootMargin || '200px 0px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#observer?.disconnect();
          if (stylesheetUrl) this.#ensureStylesheetsDeferred(stylesheetUrl);
          if (this.dataset.script) this.#ensureScriptDeferred(this.dataset.script);
          if (fetchContent) void this.load();
          break;
        }
      },
      { root: null, rootMargin, threshold: 0 }
    );

    this.#observer.observe(this);
  }

  #syncLoadedUi() {
    this.setAttribute('aria-busy', 'false');
    this.dispatchEvent(new CustomEvent('lazy-content-section:loaded', { bubbles: true }));
  }

  /**
   * @param {string} value
   */
  #ensureStylesheetsDeferred(value) {
    value
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean)
      .forEach((url) => this.#ensureStylesheetDeferred(url));
  }

  /**
   * Print-media trick — non-blocking for viewport-deferred loads.
   *
   * @param {string} url
   */
  #ensureStylesheetDeferred(url) {
    if (!url) return;

    const existing = this.#findStylesheet(url);
    if (existing) {
      void this.#waitForStylesheet(existing, url).catch(() => {});
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.media = 'print';
    link.dataset.lazyContentStylesheet = '';
    void this.#waitForStylesheet(link, url).catch(() => {});
    document.head.appendChild(link);
  }

  /** @param {string} url @returns {HTMLLinkElement | null} */
  #findStylesheet(url) {
    const selector = `link[rel="stylesheet"][href="${CSS.escape(url)}"], link[data-lazy-content-stylesheet][href="${CSS.escape(url)}"]`;
    for (const link of document.querySelectorAll(selector)) {
      if (!(link instanceof HTMLLinkElement)) continue;
      if (failedStylesheetLinks.has(link)) {
        if (link.hasAttribute('data-lazy-content-stylesheet')) link.remove();
        continue;
      }
      // An untracked link may have failed before this loader attached listeners. Only reuse a
      // ready stylesheet or a request whose load/error lifecycle this loader already observes.
      if (link.sheet || stylesheetLoadPromises.has(link)) return link;
    }
    return null;
  }

  /** @param {HTMLLinkElement} link @param {string} url @returns {Promise<void>} */
  #waitForStylesheet(link, url) {
    const pending = stylesheetLoadPromises.get(link);
    if (pending) return pending;

    const promise = new Promise((resolve, reject) => {
      const cleanup = () => {
        link.removeEventListener('load', loaded);
        link.removeEventListener('error', failed);
      };
      const loaded = () => {
        cleanup();
        if (link.hasAttribute('data-lazy-content-stylesheet') && link.media === 'print') link.media = 'all';
        resolve();
      };
      const failed = () => {
        cleanup();
        failedStylesheetLinks.add(link);
        reject(new Error(`Failed to load stylesheet: ${url}`));
      };
      link.addEventListener('load', loaded, { once: true });
      link.addEventListener('error', failed, { once: true });
      if (link.sheet) loaded();
    });
    stylesheetLoadPromises.set(link, promise);
    return promise;
  }

  /**
   * @param {string} src
   */
  #ensureScriptDeferred(src) {
    if (!src || document.querySelector(`script[src="${CSS.escape(src)}"]`)) return;

    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.dataset.lazyContentScript = '';
    document.head.appendChild(script);
  }

  /**
   * @param {string} value
   * @returns {Promise<void>}
   */
  async #ensureStylesheets(value) {
    const urls = value
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

    await Promise.all(urls.map((url) => this.#ensureStylesheet(url)));
  }

  /**
   * @param {string} url
   * @returns {Promise<void>}
   */
  #ensureStylesheet(url) {
    if (!url) return Promise.resolve();

    const existing = this.#findStylesheet(url);
    if (existing) return this.#waitForStylesheet(existing, url);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.dataset.lazyContentStylesheet = '';
    const pending = this.#waitForStylesheet(link, url);
    document.head.appendChild(link);
    return pending;
  }

  /**
   * CLS-ONAIR. A section response can carry stylesheet links inside the content it mounts (the
   * Live TV card on Home links ltv-style.css, live-tv.css and missed-auctions-filters.css there).
   * Mounted as they are, those links only start loading once the content is in the document, so
   * the content is laid out and painted without its rules and moves when they arrive. Each such
   * stylesheet (all media only) is loaded first through the same head link `data-stylesheet` uses,
   * and the content mounts once they have loaded or failed, or after INNER_STYLESHEETS_MAX_WAIT_MS.
   * The links inside the content are mounted as before, so the final cascade does not change.
   * Content without stylesheet links mounts in the same task as before. Only content that mounts
   * on screen waits like this (see #isOnScreen).
   *
   * @param {Element} inner
   * @returns {Promise<void> | null}
   */
  #innerStylesheetsPending(inner) {
    /** @type {Set<string>} */
    const hrefs = new Set();
    for (const link of inner.querySelectorAll('link[rel="stylesheet"][href]')) {
      const media = (link.getAttribute('media') ?? '').trim().toLowerCase();
      const href = link.getAttribute('href') ?? '';
      if (href && (media === '' || media === 'all')) hrefs.add(href);
    }
    if (!hrefs.size) return null;

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;
    const limit = new Promise((resolve) => {
      timer = setTimeout(resolve, INNER_STYLESHEETS_MAX_WAIT_MS);
    });
    const loads = Promise.all([...hrefs].map((href) => this.#ensureStylesheet(href).catch(() => {})));
    return Promise.race([loads, limit]).then(() => clearTimeout(timer));
  }

  /**
   * CLS-ONAIR. Whether any part of this element is inside the visible window right now. Content
   * that mounts there replaces a placeholder the shopper can see, so it waits for its own
   * stylesheets and is laid out once, styled. Content that mounts outside the window (still below
   * the screen while the page scrolls, or hidden) mounts at once, as before: a wait there would
   * give the scroll time to bring the small placeholder into view, and the full content would then
   * push everything under it down on screen. Its stylesheets load as before, after the mount.
   *
   * @returns {boolean}
   */
  #isOnScreen() {
    const box = this.getBoundingClientRect();
    const viewHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewWidth = window.innerWidth || document.documentElement.clientWidth;
    return box.bottom > 0 && box.top < viewHeight && box.right > 0 && box.left < viewWidth;
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  #ensureScript(src) {
    if (!src || document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.lazyContentScript = '';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Fetches this section’s HTML and mounts `.lazy-content-section__inner` contents into this element.
   *
   * @fires lazy-content-section:loaded
   * @fires lazy-content-section:error
   */
  #loadFromSectionApi() {
    if (!this.#contentLoadPromise) {
      this.#contentLoadPromise = this.#fetchAndMountSection();
    }
    return this.#contentLoadPromise;
  }

  async #fetchAndMountSection() {
    const rawId = this.dataset.sectionId ?? '';
    const sectionId = normalizeSectionId(rawId);
    const blockId = this.dataset.blockId ?? '';

    if (!sectionId) {
      this.#fail(new Error('lazy-content-section: missing data-section-id'));
      return;
    }

    try {
      if (this.dataset.stylesheet) {
        await this.#ensureStylesheets(this.dataset.stylesheet);
      }

      if (this.dataset.script) {
        await this.#ensureScript(this.dataset.script);
      }

      const doc = await this.#getSectionDocument(sectionId);
      await nextTask();
      const wrapper = doc.getElementById(buildSectionSelector(sectionId));
      const inner = this.#findLazyInner(wrapper, blockId);

      if (!inner) {
        this.#fail(
          new Error(
            blockId
              ? `lazy-content-section: no .lazy-content-section__inner for block ${blockId}`
              : 'lazy-content-section: no .lazy-content-section__inner in section response'
          )
        );
        return;
      }

      // CLS-RINGS: content that would land before the held theme stylesheets apply waits for them.
      const heldStylesheets = heldStylesheetsPending();
      if (heldStylesheets) await heldStylesheets;

      const cloned = inner.cloneNode(true);
      // CLS-ONAIR: content that mounts on screen waits for its own stylesheets, so it is laid out
      // once, styled. Content outside the screen mounts at once, as before.
      const onScreen = this.#isOnScreen();
      // HOME-MOUNT-STEPS: steps only before the shopper's first scroll or input (mountStepsAllowed).
      const inSteps = this.hasAttribute('data-mount-steps') && mountStepsAllowed();
      const innerStylesheets = onScreen ? this.#innerStylesheetsPending(inner) : null;
      if (innerStylesheets) await innerStylesheets;
      if (inSteps) {
        const mounted = mountStepsQueue.then(() => this.#mountInSteps(cloned, wrapper, inner));
        mountStepsQueue = mounted.then(mountStepsGap, mountStepsGap);
        await mounted;
      } else {
        this.#ensureSectionStyles(wrapper, inner);
        this.replaceChildren(...cloned.childNodes);
        this.#activateScripts(this);
      }
      delete this.dataset.error;
      this.setAttribute('data-loaded', '');
      this.setAttribute('aria-busy', 'false');
      this.dispatchEvent(new CustomEvent('lazy-content-section:loaded', { bubbles: true }));
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.#fail(err);
    }
  }

  /**
   * HOME-MOUNT-STEPS: puts the content in over several frames, at most MOUNT_STEP_MAX_ELEMENTS
   * elements a frame (a larger unit goes in alone), the first step in place of the placeholder and
   * right after the section styles, as in the one task mount. Style elements and stylesheet links go in
   * with the element that holds them (listMountUnits), so every frame shows content styled as it is at
   * the end. Its scripts run once all of it is in, in document order, as they do after the one task
   * mount, so no script meets part of the content. While the document is hidden nothing is painted, so
   * the rest goes in at once; and once the shopper scrolls or gives an input (mountStepsStopped), the
   * rest goes in at once too.
   *
   * @param {Node} cloned - The copy of the lazy inner whose children go in.
   * @param {HTMLElement | null} wrapper
   * @param {Element} inner
   * @returns {Promise<void>}
   */
  async #mountInSteps(cloned, wrapper, inner) {
    const nodes = [...cloned.childNodes];
    const styles = nodes.filter(isMountStyle);
    /** @type {MountUnit[]} */
    const units = [];
    listMountUnits(this, nodes, units);

    // The first step always runs, so the placeholder goes even when there is no content, as before.
    /** @type {MountUnit[][]} */
    const steps = [[]];
    let stepSize = styles.reduce((sum, style) => sum + mountElementCount(style), 0);
    for (const unit of units) {
      if (stepSize > 0 && stepSize + unit.size > MOUNT_STEP_MAX_ELEMENTS) {
        steps.push([]);
        stepSize = 0;
      }
      steps[steps.length - 1].push(unit);
      stepSize += unit.size;
    }

    for (const [index, step] of steps.entries()) {
      if (index === 0) {
        this.#ensureSectionStyles(wrapper, inner);
        this.replaceChildren(...styles);
      } else if (!mountStepsStopped) {
        // Once the steps have stopped, the rest goes in now, in this task.
        await afterNextFrame();
      }
      for (const { parent, node, before } of step) parent.insertBefore(node, before);
    }
    this.#activateScripts(this);
  }

  /**
   * Section API styles sit outside the lazy inner fragment. Keep them available before mounting
   * that fragment; its own inline styles stay in place with their original order and attributes.
   *
   * @param {HTMLElement | null} sectionRoot
   * @param {Element} inner
   */
  #ensureSectionStyles(sectionRoot, inner) {
    if (!sectionRoot) return;

    for (const style of sectionRoot.querySelectorAll('style[data-section-stylesheet]')) {
      if (!(style instanceof HTMLStyleElement)) continue;
      if (inner.contains(style) || style.closest('[id^="shopify-section-"]') !== sectionRoot) continue;

      const alreadyLoaded = [...document.querySelectorAll('style[data-section-stylesheet]')].some(
        (existing) =>
          existing instanceof HTMLStyleElement &&
          !this.contains(existing) &&
          !existing.disabled &&
          !existing.sheet?.disabled &&
          existing.textContent === style.textContent &&
          existing.nonce === style.nonce &&
          existing.attributes.length === style.attributes.length &&
          [...style.attributes].every(
            ({ name, value }) => name === 'nonce' || existing.getAttribute(name) === value
          )
      );
      if (alreadyLoaded) continue;

      const clone = /** @type {HTMLStyleElement} */ (style.cloneNode(true));
      // Browsers can conceal the nonce attribute value while retaining the nonce property.
      if (style.nonce) clone.nonce = style.nonce;
      document.head.appendChild(clone);
    }
  }

  /**
   * @param {string} sectionId
   * @returns {Promise<Document>}
   */
  #getSectionDocument(sectionId) {
    let promise = sectionDocumentPromises.get(sectionId);
    if (!promise) {
      promise = this.#fetchSectionHtml(sectionId).then((html) =>
        new DOMParser().parseFromString(html, 'text/html')
      );
      sectionDocumentPromises.set(sectionId, promise);
    }
    return promise;
  }

  /**
   * @param {HTMLElement | null} sectionRoot
   * @param {string} blockId
   * @returns {Element | null}
   */
  #findLazyInner(sectionRoot, blockId) {
    if (!sectionRoot) return null;

    if (blockId) {
      const lazy = sectionRoot.querySelector(
        `lazy-content-section[data-block-id="${CSS.escape(blockId)}"]`
      );
      return lazy?.querySelector('.lazy-content-section__inner') ?? null;
    }

    const lazy =
      sectionRoot.querySelector('lazy-content-section[data-section-id]:not([data-block-id])') ||
      sectionRoot.querySelector('lazy-content-section');

    return lazy?.querySelector('.lazy-content-section__inner') ?? null;
  }

  /**
   * @param {string} sectionId
   * @returns {Promise<string>}
   */
  async #fetchSectionHtml(sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();

    // LAZY-EARLY (sections/cx-container.liquid, theme setting perf_cx_lazy_early_fetch): a section
    // near the top may already have asked for this exact address while the page was being read.
    // Each early request is used once; without one, the section asks now, as before.
    const win = /** @type {any} */ (window);
    const early = win.lazySectionEarlyFetches?.[url.toString()];
    if (early) delete win.lazySectionEarlyFetches[url.toString()];

    const response = await (early ||
      fetch(url.toString(), {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      }));

    if (!response.ok) {
      throw new Error(`Section HTTP ${response.status}`);
    }

    return response.text();
  }

  /**
   * Re-run inline scripts injected via Section Rendering API (they do not execute from DOMParser alone).
   *
   * @param {ParentNode} root
   */
  #activateScripts(root) {
    root.querySelectorAll('script').forEach((script) => {
      if (isRunOnceScriptOnPage(script)) return;
      const clone = document.createElement('script');
      for (const { name, value } of script.attributes) {
        clone.setAttribute(name, value);
      }
      clone.textContent = script.textContent;
      script.replaceWith(clone);
    });
  }

  /**
   * @param {Error} error
   */
  #fail(error) {
    this.#contentLoadPromise = null;
    this.setAttribute('aria-busy', 'false');
    this.dataset.error = error.message;
    this.dispatchEvent(
      new CustomEvent('lazy-content-section:error', { bubbles: true, detail: { error } })
    );
  }
}

if (!customElements.get('lazy-content-section')) {
  customElements.define('lazy-content-section', LazyContentSection);
}
